interface ChatMessageProps {
    message: string;
    timestamp?: string;
    isMyMessage: boolean;
    authorNickname: string;
}

/**
 * Format timestamp to Korean locale time string
 * @param {string} [timestamp] - ISO timestamp string
 * @returns {string} Formatted time string or '방금' if no timestamp
 */
const formatTimestamp = (timestamp?: string): string => {
    if (!timestamp) return '방금';
    return new Date(timestamp).toLocaleTimeString('ko-KR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
};

/**
 * Chat message bubble component with different styling for own vs others' messages
 * @param {ChatMessageProps} props - Component props
 * @param {string} props.message - Message content
 * @param {string} [props.timestamp] - Message timestamp
 * @param {boolean} props.isMyMessage - Whether message is from current user
 * @param {string} props.authorNickname - Author's nickname
 * @returns {JSX.Element} Chat message bubble
 */
export const ChatMessage = ({ message, timestamp, isMyMessage, authorNickname }: ChatMessageProps) => {
    const formattedTime = formatTimestamp(timestamp);

    if (isMyMessage) {
        return (
            <div className="w-full flex items-end justify-end gap-[6px] py-[6px]">
                <div className="text-[11px] text-[#84888F]">{formattedTime}</div>
                <div className="max-w-[70%] py-[6px] px-3 rounded-xl bg-[#081837] text-white text-sm">{message}</div>
            </div>
        );
    }

    return (
        <div className="pt-[6px]">
            <div className="shrink-0 flex items-center gap-[6px]">
                <div className="w-6 h-6 bg-[#53555B] rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{authorNickname?.charAt(0) || 'U'}</span>
                </div>
                <div className="text-xs text-[#53555B]">{authorNickname}</div>
            </div>
            <div className="w-full flex items-end gap-[6px] py-[6px]">
                <div className="max-w-[70%] py-[6px] px-3 rounded-xl bg-[#f4f5f5] text-[#081837] text-sm">
                    {message}
                </div>
                <div className="text-[11px] text-[#84888F]">{formattedTime}</div>
            </div>
        </div>
    );
};
